import { AlertCircle, MoreVerticalIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useRemoveTag } from "@/features/recording-tags/api/remove-tag";
import { PublicTag } from "@classview/shared-types";
import { useQueryClient } from "@tanstack/react-query";

import { fetchRecordingTagsKey } from "../api/fetch-recording-tags";

type RecordingTagsRowActionsProps = {
  tagId: string;
  organisationId: string;
};

export function RecordingTagsRowActions(props: RecordingTagsRowActionsProps) {
  const { organisationId, tagId } = props;
  const queryClient = useQueryClient();

  const {
    mutateAsync: deleteTag,
    isPending,
    isSuccess,
    isError,
    error,
    reset,
  } = useRemoveTag({
    onSuccess: () => {
      queryClient.setQueryData(
        fetchRecordingTagsKey({ organisationId: organisationId }),
        (oldData: PublicTag[]) => oldData.filter((tag) => tag.id !== tagId),
      );
    },
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!isDeleting) {
      reset();
    }
  }, [isDeleting, reset]);

  const handleDelete = async () => {
    try {
      await deleteTag({
        orgId: organisationId,
        tagId: tagId,
      });
      setIsDeleting(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dialog onOpenChange={setIsDeleting} open={isDeleting}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DialogTrigger asChild>
            <DropdownMenuItem className="text-destructive">
              Delete
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
          <DialogDescription>
            This action cannot be undone. Are you sure you want to permanently
            delete this tag?
          </DialogDescription>
        </DialogHeader>
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending || isSuccess}
            isLoading={isPending || isSuccess}
            isAnimated
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
